import { createContext } from "react";

export type AppPreferences = {
  theme: AppThemePreference;
  showExpertDetailsByDefault: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
};

export type AppThemePreference = "system" | "light" | "dark";

export type AppPreferencesContextValue = {
  preferences: AppPreferences;
  updatePreference<K extends keyof AppPreferences>(key: K, value: AppPreferences[K]): void;
  resetPreferences(): void;
};

const STORAGE_KEY = "6529-safe-app.preferences";

export const DEFAULT_PREFERENCES: AppPreferences = {
  theme: "system",
  showExpertDetailsByDefault: false,
  reducedMotion: false,
  highContrast: false
};

export const AppPreferencesContext = createContext<AppPreferencesContextValue | undefined>(
  undefined
);

export function readStoredPreferences(): AppPreferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<AppPreferences>;
    return {
      theme: isAppThemePreference(parsed.theme) ? parsed.theme : DEFAULT_PREFERENCES.theme,
      showExpertDetailsByDefault:
        parsed.showExpertDetailsByDefault ?? DEFAULT_PREFERENCES.showExpertDetailsByDefault,
      reducedMotion: parsed.reducedMotion ?? DEFAULT_PREFERENCES.reducedMotion,
      highContrast: parsed.highContrast ?? DEFAULT_PREFERENCES.highContrast
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function isAppThemePreference(value: unknown): value is AppThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function writeStoredPreferences(preferences: AppPreferences): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage can be blocked or quota-limited. Preference state should remain usable in memory.
  }
}
